/**
 * New Topic Page - Store
 * 
 * Modern form to create a new topic with user info sidebar.
 */

import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useStore } from '@/contexts/StoreContext'
import { useTheme } from '@/contexts/ThemeContext'
import { usePlayerAuth } from '@/contexts/PlayerAuthContext'
import forumStoreService, { type ForumBoard } from '@/lib/services/forumStore.service'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ArrowLeft, Send, Lock, LogIn, User, MessageCircle, Trophy, Calendar, Shield } from 'lucide-react'
import PageHero from '@/components/common/PageHero'
import { toast } from 'sonner'
import { useAuth } from '@/contexts/AuthContext'
import { Caixa } from '@/construtor/caixa'

export default function NewTopicPage() {
  const { store } = useStore()
  const { config } = useTheme()
  const { isAuthenticated, player } = usePlayerAuth()
  const { categorySlug, boardSlug } = useParams<{
    categorySlug: string
    boardSlug: string
  }>()
  const navigate = useNavigate()
  const { openLoginModal } = useAuth()

  const [board, setBoard] = useState<ForumBoard | null>(null)
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (categorySlug && boardSlug && store?.id) {
      loadBoard()
    }
  }, [categorySlug, boardSlug, store])

  async function loadBoard() {
    if (!store?.id || !categorySlug || !boardSlug) return
    
    try {
      const data = await forumStoreService.getBoardBySlug(
        store.id,
        categorySlug,
        boardSlug,
      )
      setBoard(data)

      if (data.isLocked) {
        toast.warning('🔒 Board bloqueado - Este board não aceita novos tópicos.')
        navigate(`/forum/${categorySlug}/${boardSlug}`)
      }
    } catch (error) {
      console.error('Erro ao carregar board:', error)
      toast.error('❌ Não foi possível carregar as informações do board.')
      navigate('/forum')
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    if (!title.trim() || !content.trim() || !board) return

    if (content.trim().length < 20) {
      toast.warning('⚠️ O conteúdo deve ter pelo menos 20 caracteres.')
      return
    }

    try {
      setSubmitting(true)
      console.log('[NewTopic] Criando tópico...')
      console.log('[NewTopic] Board ID:', board.id)
      console.log('[NewTopic] Title:', title.trim())
      console.log('[NewTopic] Content length:', content.trim().length)
      console.log('[NewTopic] Player token exists:', !!localStorage.getItem('playerToken'))
      
      const topic = await forumStoreService.createTopic(store!.id, {
        boardId: board.id,
        title: title.trim(),
        content: content.trim(),
      })

      console.log('[NewTopic] Tópico criado com sucesso:', topic)
      
      toast.success('Tópico criado! Seu tópico foi publicado com sucesso.')

      navigate(`/forum/${categorySlug}/${boardSlug}/${topic.slug}`)
    } catch (error: any) {
      console.error('[NewTopic] Erro ao criar tópico:', error)
      console.error('[NewTopic] Error response:', error.response?.data)
      console.error('[NewTopic] Error status:', error.response?.status)
      
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          'Erro desconhecido ao criar tópico'
      
      toast.error(`❌ Erro ao criar tópico: ${errorMessage}`)
    } finally {
      setSubmitting(false)
    }
  }

  if (!board) {
    return (
      <div>
        <PageHero
          title="Criar Novo Tópico"
          breadcrumbs={[
            { label: 'Fórum', href: '/forum' },
            { label: 'Carregando...' },
          ]}

        />
        <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div>
      {/* Hero */}
      <PageHero
        title={`Criar Novo Tópico em ${board.name}`}
        breadcrumbs={[
          { label: 'Fórum', href: '/forum' },
          { label: board.name, href: `/forum/${categorySlug}/${boardSlug}` },
          { label: 'Novo Tópico' },
        ]}

      />

      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8 max-w-7xl -mt-24 pt-24 relative z-10">
        {!isAuthenticated ? (
          <Caixa id="forum.novo.entrar" miolo>
          <Card>
            <CardContent className="p-12">
              <div className="py-12 text-center">
                <div className="mb-6 flex justify-center">
                  <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center">
                    <Lock className="w-10 h-10 text-primary" />
                  </div>
                </div>
                <h2 className="text-2xl font-bold mb-3 text-foreground">
                  Autenticação Necessária
                </h2>
                <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                  Você precisa estar autenticado para criar um novo tópico. Faça login ou crie uma conta para participar das discussões.
                </p>
                <div className="flex items-center justify-center gap-4">
                  <Button
                    onClick={openLoginModal}
                    size="lg"
                  >
                    <LogIn className="w-5 h-5 mr-2" />
                    Fazer Login
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate(`/forum/${categorySlug}/${boardSlug}`)}
                  >
                    Voltar ao Board
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
          </Caixa>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Sidebar - User Info */}
            <div className="lg:col-span-3">
              <Caixa id="forum.novo.autor">
              <Card className="border-card-border sticky top-24 overflow-hidden">
                {/* Card Header with gradient */}
                <div className="bg-gradient-to-br from-primary via-accent to-primary p-6 relative">
                  <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMTZjMC0xLjEtLjktMi0yLTJoLTJjLTEuMSAwLTIgLjktMiAydjJjMCAxLjEuOSAyIDIgMmgyYzEuMSAwIDItLjkgMi0ydi0yem0wIDI0YzAtMS4xLS45LTItMi0yaC0yYy0xLjEgMC0yIC45LTIgMnYyYzAgMS4xLjkgMiAyIDJoMmMxLjEgMCAyLS45IDItMnYtMnptLTI0IDBjMC0xLjEtLjktMi0yLTJoLTJjLTEuMSAwLTIgLjktMiAydjJjMCAxLjEuOSAyIDIgMmgyYzEuMSAwIDItLjkgMi0ydi0yem0wLTI0YzAtMS4xLS45LTItMi0yaC0yYy0xLjEgMC0yIC45LTIgMnYyYzAgMS4xLjkgMiAyIDJoMmMxLjEgMCAyLS45IDItMnYtMnoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-30" />
                  <div className="relative flex flex-col items-center">
                    {/* Avatar */}
                    <div className="w-24 h-24 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white font-bold text-3xl shadow-2xl ring-4 ring-white/30 mb-3">
                      {player?.name ? player.name[0].toUpperCase() : '?'}
                    </div>
                    <h3 className="text-xl font-bold text-white text-center">
                      {player?.name || 'Jogador'}
                    </h3>
                    <p className="text-sm text-white/80">
                      @{player?.nick || player?.name?.toLowerCase() || 'jogador'}
                    </p>
                  </div>
                </div>

                <CardContent className="p-6">
                  {/* User Badges */}
                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Shield className="w-4 h-4 text-primary" />
                      <span className="text-sm font-semibold text-foreground">Badges</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Badge 
                        variant="secondary" 
                        className="bg-primary/20 text-primary border-primary/30"
                      >
                        🆕 Novato
                      </Badge>
                      <Badge
                        variant="secondary"
                        className="bg-success/20 text-success border-success/30"
                      >
                        ✍️ Autor
                      </Badge>
                    </div>
                  </div>

                  <div className="border-t border-border pt-6">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 bg-primary/5 rounded-lg border border-primary/10">
                        <div className="flex items-center gap-2">
                          <MessageCircle className="w-4 h-4 text-primary" />
                          <span className="text-sm text-muted-foreground">Posts</span>
                        </div>
                        <span className="font-bold text-foreground">0</span>
                      </div>

                      <div className="flex items-center justify-between p-3 bg-accent/5 rounded-lg border border-accent/10">
                        <div className="flex items-center gap-2">
                          <Trophy className="w-4 h-4 text-accent" />
                          <span className="text-sm text-muted-foreground">Reações</span>
                        </div>
                        <span className="font-bold text-foreground">0</span>
                      </div>

                      <div className="flex items-center justify-between p-3 bg-background-secondary rounded-lg">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">Membro</span>
                        </div>
                        <span className="font-bold text-foreground text-xs">2026</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              </Caixa>
            </div>

            {/* Main Content - Create Topic Form */}
            <div className="lg:col-span-9">
              <Caixa id="forum.novo.formulario" miolo>
              <Card className="border-card-border shadow-glow">
                <CardContent className="p-8">
                  {/* Header */}
                  <div
                    className="border-l-4 pl-4 mb-8"
                    style={{ borderColor: board.color }}
                  >
                    <h1 className="text-3xl font-bold mb-2 text-foreground">Criar Novo Tópico</h1>
                    <p className="text-muted-foreground">
                      em{' '}
                      <span 
                        className="font-medium"
                        style={{ color: board.color }}
                      >
                        {board.icon} {board.name}
                      </span>
                    </p>
                  </div>

                  {/* Form */}
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Title Input */}
                    <Caixa id="forum.novo.titulo">
                    <div>
                      <label htmlFor="title" className="block text-sm font-medium mb-2 text-foreground">
                        Título do Tópico *
                      </label>
                      <input
                        id="title"
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Ex: Como fazer isso..."
                        className="w-full bg-background-secondary border border-border rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-foreground transition-all"
                        required
                        maxLength={200}
                      />
                      <p className="text-sm text-muted-foreground mt-2">
                        {title.length}/200 caracteres
                      </p>
                    </div>
                    </Caixa>

                    {/* Content Textarea */}
                    <Caixa id="forum.novo.conteudo">
                    <div>
                      <label htmlFor="content" className="block text-sm font-medium mb-2 text-foreground">
                        Conteúdo *
                      </label>
                      <textarea
                        id="content"
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        placeholder="Descreva sua pergunta ou discussão em detalhes... Seja claro, respeitoso e construtivo!"
                        className="w-full bg-background-secondary border border-border rounded-xl px-4 py-4 min-h-[320px] focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-foreground resize-none transition-all"
                        required
                      />
                      <p className="text-sm text-muted-foreground mt-2">
                        Mínimo 20 caracteres. Seja claro e detalhado.
                      </p>
                    </div>
                    </Caixa>

                    {/* Action Buttons */}
                    <Caixa id="forum.novo.botoes">
                    <div className="flex items-center gap-4 pt-4 border-t border-border">
                      <Button
                        type="submit"
                        disabled={submitting || !title.trim() || !content.trim() || content.length < 20}
                        size="lg"
                        className="shadow-lg hover:shadow-glow transition-all"
                      >
                        {submitting ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Criando...
                          </>
                        ) : (
                          <>
                            <Send className="w-5 h-5" />
                            Criar Tópico
                          </>
                        )}
                      </Button>

                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => navigate(`/forum/${categorySlug}/${boardSlug}`)}
                      >
                        Cancelar
                      </Button>
                    </div>
                    </Caixa>
                  </form>
                </CardContent>
              </Card>
              </Caixa>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
